import { useState } from "react";
import { useT } from "../i18n";
import { FehlerBand, Knopf } from "./index";
import { useSitzung } from "../schale/Sitzung";

/**
 * **Einen fehlenden Eintrag dort anlegen, wo er fehlt** — Interview F16 vom
 * 04.10.2026 (Befund S-36).
 *
 * Absagegründe und Notizarten stehen unter Weiteres in den Stammdaten, und die
 * darf der Vertrieb nicht öffnen. Gebraucht werden sie aber gerade von ihm:
 * im Absageblatt, im Stornoblatt, über der Gesprächsnotiz. Luca hat
 * entschieden: *„Grund anlegen" direkt im Blatt, ohne Zugang zu den
 * Stammdaten.* Die API ließ das Anlegen jeder Rolle schon zu; es fehlte nur
 * der Griff an der Stelle, an der man merkt, dass etwas fehlt.
 *
 * Ein Wort genügt — den Code leitet die API ab (Regel 11). Nach dem Anlegen
 * ist der neue Eintrag gewählt (`nachher`).
 */
export function KatalogAnlegen({ griff, platz, anlegen, nachher }: {
  /** i18n-Schlüssel des Griffs, etwa `grundwahl.anlegen`. */
  griff: string;
  /** i18n-Schlüssel des Platzhalters im Feld. */
  platz: string;
  /** Legt an und nennt den Code. */
  anlegen: (bezeichnung: string) => Promise<string>;
  nachher: (code: string) => void;
}) {
  const t = useT();
  /* Dieselbe Frage wie die API (`alleRollen` in routen/stamm.ts): jede
     Rolle außer dem technischen Außendienst. */
  const darfAnlegen = useSitzung().darf(["disposition", "aussendienst", "buchhaltung", "verwaltung"]);
  const [offen, setzeOffen] = useState(false);
  const [wort, setzeWort] = useState("");
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<unknown>(null);

  if (!darfAnlegen) return null;
  if (!offen) {
    return (
      <Knopf art="stumm" klein type="button" onClick={() => setzeOffen(true)}>
        {t(griff)}
      </Knopf>
    );
  }

  const los = () => {
    const bezeichnung = wort.trim();
    if (!bezeichnung || laeuft) return;
    setzeLaeuft(true);
    setzeFehler(null);
    anlegen(bezeichnung).then(
      (code) => {
        setzeLaeuft(false);
        setzeOffen(false);
        setzeWort("");
        nachher(code);
      },
      (f: unknown) => { setzeLaeuft(false); setzeFehler(f); },
    );
  };

  return (
    <div className="katalog-anlegen" style={{ marginTop: 8 }}>
      <input
        className="feld"
        value={wort}
        maxLength={120}
        placeholder={t(platz)}
        aria-label={t(griff)}
        onChange={(e) => setzeWort(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); los(); }
          if (e.key === "Escape") { setzeOffen(false); setzeWort(""); }
        }}
      />
      <div className="knopfreihe">
        <Knopf klein type="button" einmal disabled={!wort.trim() || laeuft} onClick={los}>
          {t("katalogAnlegen.anlegen")}
        </Knopf>
        <Knopf art="stumm" klein type="button" onClick={() => { setzeOffen(false); setzeWort(""); setzeFehler(null); }}>
          {t("allgemein.dochNicht")}
        </Knopf>
      </div>
      {fehler ? <FehlerBand fehler={fehler} /> : null}
    </div>
  );
}
